import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type CongressContentCardProps = {
  /** Small uppercase label above the title (e.g. the PDF label or congress name) */
  label: string | null;
  /** Bold title */
  title: string;
  /** Middle hint line, e.g. "Tap to read" */
  tapHint: string | null;
  /** Bottom-right detail, e.g. "3 publications" or "Booth #200" */
  detail: string | null;
  /** Optional icon before the detail */
  detailIcon: ReactNode | null;
  /** Left thumbnail; falls back to `fallbackIcon` on a tinted placeholder */
  imageUrl: string | null;
  /** Shown in the thumbnail slot when there is no image */
  fallbackIcon: ReactNode | null;
  /**
   * `light`: the canonical blackberry card (PDFs, attached content).
   * `dark`: accent-filled card, white text (thematic collections).
   */
  tone: "light" | "dark";
  /** Fill colour for the `dark` tone (the congress cover colour) */
  accentColor: string;
  onClick: () => void;
};

export function CongressContentCard({
  label,
  title,
  tapHint,
  detail,
  detailIcon,
  imageUrl,
  fallbackIcon,
  tone,
  accentColor,
  onClick,
}: CongressContentCardProps): React.JSX.Element {
  const dark = tone === "dark";
  const accentTextClass = dark ? "text-white/85" : "text-ds-blackberry-600";

  return (
    <button
      type="button"
      onClick={onClick}
      style={dark ? { backgroundColor: accentColor } : undefined}
      className={cn(
        "flex min-h-28 w-full cursor-pointer overflow-hidden rounded-2xl text-left font-dm-sans",
        dark
          ? "text-white"
          : "border border-ds-blackberry-100 bg-ds-blackberry-50"
      )}
    >
      <div
        className={cn(
          "flex w-24 shrink-0 items-center justify-center self-stretch",
          dark ? "bg-black/10" : "bg-ds-blackberry-100 text-ds-blackberry-600"
        )}
      >
        {imageUrl !== null ? (
          <img src={imageUrl} alt="" className="size-full object-cover" />
        ) : (
          fallbackIcon
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between p-4">
        <div className="flex flex-col gap-0.5">
          {label !== null && label !== "" && (
            <span
              className={cn(
                "text-xs font-semibold uppercase tracking-wide",
                accentTextClass
              )}
            >
              {label}
            </span>
          )}
          <span
            className={cn(
              "line-clamp-2 text-sm font-bold",
              dark ? "text-white" : "text-ds-black"
            )}
          >
            {title}
          </span>
          {tapHint !== null && tapHint !== "" && (
            <span className={cn("text-xs", accentTextClass)}>{tapHint}</span>
          )}
        </div>
        {detail !== null && detail !== "" && (
          <div
            className={cn(
              "flex items-center justify-end gap-1 text-xs",
              accentTextClass
            )}
          >
            {detailIcon}
            {detail}
          </div>
        )}
      </div>
    </button>
  );
}

export type { CongressContentCardProps };
